import { createRef, useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Nav from 'react-bootstrap/Nav';
import PropTypes from 'prop-types';
import TabContainer from 'react-bootstrap/TabContainer';
import TabContent from 'react-bootstrap/TabContent';
import TabPane from 'react-bootstrap/TabPane';
import TextTruncate from 'react-text-truncate';

import CardModule from '../../shared/card-module';
import ModalBasic from '../../../atoms/modal-basic';
import format from '../../../../utils/format';
import styles from './portfolio-management.module.scss';
import usePortfolioManagement from '../../../../stores/profile/modules/use-portfolio-management';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import { urls } from '../../../../utils/url-helper';

const className = augmentClassName(styles);

const PortfolioManagement = ({ setRenderPortfolioManagement }) => {
  const { managers, defaultManager, ticker, renderPortfolioManagement } = usePortfolioManagement();
  const [modalManager, setModalManager] = useState();
  const [truncateRefs, setTruncateRefs] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  setRenderPortfolioManagement && setRenderPortfolioManagement(renderPortfolioManagement);

  useEffect(() => {
    setTruncateRefs(lastRefs =>
      Array(managers.length)
        .fill()
        .map((m, i) => lastRefs[i] || createRef())
    );
  }, [managers.length]);

  useEffect(() => {
    truncateRefs[activeTabIndex] &&
      truncateRefs[activeTabIndex].current &&
      setTimeout(() => truncateRefs[activeTabIndex].current.update());
  }, [truncateRefs, activeTabIndex]);

  const handleShowModal = setModalManager;
  const handleCloseModal = e => setModalManager(null);

  return (
    <CardModule
      data-analytics-componenttype="half-width-component"
      data-analytics-id="HW_PortfolioManagement"
      data-analytics-section="component"
    >
      <CardModule.Header>Portfolio Management</CardModule.Header>
      <CardModule.Body>
        <span {...className('sr-only')}>Portfolio Management for {ticker}</span>
        {managers && defaultManager && (
          <TabContainer defaultActiveKey={defaultManager.name} transition={false}>
            <Nav {...className('d-flex', 'flex-row', 'pl-0', 'selection-nav')}>
              {managers.map((manager, index) => {
                return (
                  <Nav.Item
                    data-key={`${manager.name || ''}-${index}`}
                    key={`${manager.name || ''}-${index}`}
                    {...className('select-container')}
                  >
                    <Nav.Link
                      eventKey={manager.name}
                      onSelect={() => setActiveTabIndex(index)}
                      {...className('select', 'p-0')}
                    >
                      <img
                        src={urls.portfolioManager.cdn.image(manager.imageFileName)}
                        alt={manager.name}
                      />
                    </Nav.Link>
                  </Nav.Item>
                );
              })}
            </Nav>
            <TabContent>
              {managers.map((manager, index) => {
                return (
                  <TabPane
                    data-key={`${manager.name || ''}-${index}`}
                    key={`${manager.name || ''}-${index}`}
                    eventKey={manager.name}
                  >
                    <h6 {...className('font-family-agenda')}>{format.text(manager.name)}</h6>
                    {manager.title && <p {...className('title')}>{manager.title}</p>}
                    <p {...className('bio', 'd-none', 'd-md-block', 'd-print-block')}>
                      {format.text(manager.bio)}
                    </p>
                    {index === activeTabIndex && (
                      <div {...className('d-md-none')}>
                        <TextTruncate
                          {...className('bio')}
                          line={5}
                          ref={truncateRefs[index]}
                          text={format.text(manager.bio)}
                          truncateText="..."
                          textTruncateChild={
                            <Button
                              variant="link--reverse-underline"
                              onClick={() => handleShowModal(manager)}
                              {...className('text-black', 'p-0', 'd-inline')}
                            >
                              View Full Biography
                            </Button>
                          }
                        />
                        <ModalBasic show={!!modalManager} onHide={handleCloseModal}>
                          <ModalBasic.Header>
                            <ModalBasic.Title>
                              Portfolio Management
                              <ModalBasic.Subtitle>
                                {format.text(modalManager && modalManager.name)}
                              </ModalBasic.Subtitle>
                            </ModalBasic.Title>
                            <ModalBasic.CloseButton onClick={handleCloseModal} />
                          </ModalBasic.Header>
                          <ModalBasic.Body>
                            {format.text(modalManager && modalManager.bio)}
                          </ModalBasic.Body>
                        </ModalBasic>
                      </div>
                    )}
                  </TabPane>
                );
              })}
            </TabContent>
          </TabContainer>
        )}
      </CardModule.Body>
      <CardModule.Footer />
    </CardModule>
  );
};

PortfolioManagement.propTypes = {
  ticker: PropTypes.string,
  setRenderPortfolioManagement: PropTypes.func
};

PortfolioManagement.defaultProps = {
  ticker: ''
};

export default PortfolioManagement;
